// Use relative path when built with Docker, localhost for development
const API_BASE_URL = process.env.REACT_APP_API_URL || (
  process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000'
);

// Callback for when session expires - will be set by App.js
let onSessionExpired = null;

export const setSessionExpiredCallback = (callback) => {
  onSessionExpired = callback;
};

const clearSession = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
};

// Single-flight refresh: concurrent 401s share one refresh request instead of
// firing N refreshes (which would rotate the refresh token N times and race).
let refreshPromise = null;

const refreshAccessToken = () => {
  if (refreshPromise) return refreshPromise;
  const refresh_token = localStorage.getItem('refresh_token');

  refreshPromise = (async () => {
    if (!refresh_token) return null;
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token }),
      });
      if (!res.ok) return null;
      const data = await res.json().catch(() => ({}));
      if (!data.token) return null;
      localStorage.setItem('token', data.token);
      if (data.refresh_token) {
        localStorage.setItem('refresh_token', data.refresh_token);
      }
      return data.token;
    } catch (error) {
      console.error('Silent refresh failed:', error);
      return null;
    } finally {
      // Allow a later refresh attempt once this one settles.
      setTimeout(() => { refreshPromise = null; }, 0);
    }
  })();

  return refreshPromise;
};

class ApiClient {
  constructor(baseURL) {
    this.baseURL = baseURL;
    this.headers = {
      'Content-Type': 'application/json',
    };
  }

  async request(endpoint, options = {}, isRetry = false) {
    const url = `${this.baseURL}${endpoint}`;
    
    // Add auth token if available
    const token = localStorage.getItem('token');
    const headers = { ...this.headers, ...options.headers };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      // Handle non-JSON responses
      const contentType = response.headers.get('content-type');
      let data;
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      if (!response.ok) {
        // Access token expired: try a silent refresh once, then retry the
        // original request with the new token. Only if that also fails do we
        // surface the session-expired modal.
        const isExpired = response.status === 401 && (
          data?.message === 'Token has expired' || data?.message === 'Invalid token'
        );
        if (isExpired && !isRetry) {
          const newToken = await refreshAccessToken();
          if (newToken) {
            return this.request(endpoint, options, true);
          }
          console.warn('Session expired - showing modal');
          clearSession();
          if (onSessionExpired) {
            onSessionExpired();
          }
          throw new Error('Token has expired');
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return { data, status: response.status };
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  async get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' });
  }

  async post(endpoint, body, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async put(endpoint, body, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  async delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'DELETE' });
  }
}

const api = new ApiClient(API_BASE_URL);

export default api;
